import { Centrifuge, type PublicationContext } from "centrifuge";
import { useEffect, useRef } from "react";

import { __WEBSOCKET_SERVER_URL__ } from "../config";

import { useCentrifugeStore } from "@/entities/session/models/store/useCentrifugeStore";

export const useCentrifugeConnect = (accessToken: string) => {
    const setCentrifuge = useCentrifugeStore((state) => state.setCentrifuge);

    useEffect(() => {
        if (!accessToken) return;

        const centrifuge = new Centrifuge(__WEBSOCKET_SERVER_URL__, { token: accessToken });

        setCentrifuge(centrifuge);
        centrifuge.connect();

        return () => {
            centrifuge.disconnect();

            if (useCentrifugeStore.getState().centrifuge === centrifuge) {
                setCentrifuge(null);
            }
        };
    }, [accessToken, setCentrifuge]);
};

export const useCentrifugeSubscription = <T>(channel: string, onPublication: (data: T) => void) => {
    const centrifuge = useCentrifugeStore((state) => state.centrifuge);
    const onPublicationRef = useRef(onPublication);

    useEffect(() => {
        onPublicationRef.current = onPublication;
    }, [onPublication]);

    useEffect(() => {
        if (!centrifuge || !channel) return;

        const existingSubscription = centrifuge.getSubscription(channel);
        const subscription = existingSubscription ?? centrifuge.newSubscription(channel);
        const handlePublication = (context: PublicationContext) => {
            onPublicationRef.current(context.data as T);
        };

        subscription.on("publication", handlePublication);

        if (!existingSubscription) {
            subscription.subscribe();
        }

        return () => {
            subscription.off("publication", handlePublication);

            if (!existingSubscription) {
                subscription.unsubscribe();
                centrifuge.removeSubscription(subscription);
            }
        };
    }, [centrifuge, channel]);
};
